import { Thunk } from "../common";
import {
  DetectionAction,
  DetectionFilterInput,
  DetectionCountOptions,
  DetectionCountHistogram,
  DetectionCountAbacus,
  DetectionCountAPIResStandard,
  DetectionCountAPIResAnimal,
  DetectionDownloadJob,
  DetectionCountAbacusAnimal,
} from "./detection-types";
import { customSnackbar, removeSnackbar, snackbarError } from "../snackbar/snackbar-actions";
import { axios, callGqlApi } from "../../helpers/api";
import gql from "../gqlTag";
import { handleGqlErrors } from "../gql-error/gql-error-actions";
import { createDownloadFromBlob } from "../files/files-actions";
import { isEqual } from "lodash";
import { sleep } from "../../helpers/common";
import { formatDateTimeFilename } from "../../helpers/time";
import { GQL_SERVER_URL } from "../../config";
import { SnackBarAction } from "../snackbar/snackbar-types";
import { Animal } from "../animals/animals-types";
const DOWNLOAD_SNACKBAR_KEY = "detection-download";

export function queryDetectionSummary(): Thunk<void, DetectionAction> {
  return async dispatch => {
    dispatch({ type: "DETECTION_SUMMARY_START_LOADING_DATA" });
    const summaryQuery = gql`
      query {
        detectionSummary {
          totalDetections
          uniqueIds
          uniqueReceivers
          idSummary {
            idCount {
              displayId
              count
              animalIds
            }
            receivers {
              serial
              deviceId
            }
          }
          receiverSummary {
            count
            receiver {
              serial
              deviceId
            }
            idCounts {
              displayId
              count
              animalIds
            }
          }
        }
      }
    `;
    callGqlApi(summaryQuery)
      .then(res => dispatch({ type: "DETECTION_SUMMARY_SET", payload: res.detectionSummary }))
      .catch(errors => {
        console.error(errors);
        dispatch({ type: "DETECTION_SUMMARY_ERROR" });
        dispatch(
          snackbarError(`There was an error fetching detection data. Please try again later`) as any
        );
      });
  };
}

/** Queries the detectionCountsPerInterval gql endpoint and returns the result. Errors are
 * intentionally not caught so that the calling function can handle it with dispatch.
 *
 * The result is left in its "pure" state because the caller may wish to parse results differently
 */
async function fetchDetectionCountsPerIntervalGql(
  filters: DetectionFilterInput,
  options: DetectionCountOptions
) {
  const detCountsResult: {
    detectionCountsPerInterval: {
      periodMs: number[];
      count: number[];
      serial?: string[];
      fullId?: string[];
    };
  } = await callGqlApi(
    gql`
      query detectionCountsPerInterval(
        $filters: DetectionFilterInput!
        $options: DetectionCountOptions!
      ) {
        detectionCountsPerInterval(filters: $filters, options: $options) {
          ... on DetectionCountsPerInterval {
            periodMs
            count
            serial
            fullId
          }
          ... on DetectionCountsPerIntervalAnimal {
            periodMs
            count
            animalId
            fullIds
          }
        }
      }
    `,
    { filters, options }
  );
  return detCountsResult.detectionCountsPerInterval;
}

export function fetchDetectionCountsHistogram(
  filters: DetectionFilterInput,
  histogramDataOptions: DetectionCountOptions
): Thunk<void, DetectionAction> {
  return async (dispatch, getState) => {
    // Early exit: if both receiver and transmitter arrays are empty, set no data and skip fetch
    if (
      filters.includeReceiverSerials?.length === 0 &&
      filters.includeTransmitterIDs?.length === 0
    ) {
      dispatch({ type: "DETECTION_SET_HISTOGRAM", payload: { detCountsHistogram: [], filters } });
      return;
    }
    if (getState().detection.histogram.status === "UNLOADED") {
      dispatch({ type: "DETECTION_SET_HISTOGRAM_INIT_LOADING", payload: { filters } });
    } else {
      dispatch({ type: "DETECTION_SET_HISTOGRAM_LOADING", payload: { filters } });
    }
    const detCountsHistogram: DetectionCountHistogram[] = [];

    let detCountsFetched: {
      periodMs: number[];
      count: number[];
    };

    try {
      detCountsFetched = await fetchDetectionCountsPerIntervalGql(filters, histogramDataOptions);
    } catch (ex) {
      dispatch(handleGqlErrors(ex));
      return;
    }

    try {
      if (detCountsFetched?.periodMs?.length && detCountsFetched?.count?.length) {
        detCountsFetched.periodMs.forEach((dt, idx) => {
          detCountsHistogram.push({
            dt,
            count: detCountsFetched.count[idx],
          });
        });
      }

      dispatch({
        type: "DETECTION_SET_HISTOGRAM",
        payload: { detCountsHistogram, filters },
      });
    } catch (ex) {
      console.error("fetchDetectionCountsHistogram failed to process data", ex);
    }
  };
}

function abacusCountsAnimal(
  detCountsFetched: DetectionCountAPIResAnimal,
  animals: Animal[]
): { countPerGroup: DetectionCountAbacusAnimal[] } {
  const animalNamesSet = new Set<string>();
  const countPerGroup: DetectionCountAbacusAnimal[] = [];

  detCountsFetched.periodMs.forEach((dt, idx) => {
    const animalId = detCountsFetched.animalId[idx];
    if (animalId) {
      const animalName: string =
        animals.find(a => a.id === animalId)?.name || `Unnamed-${animalId}`;
      animalNamesSet.add(animalName);

      const txs = detCountsFetched.fullIds[idx] || [];

      countPerGroup.push({
        dt,
        count: detCountsFetched.count[idx],
        txs,
        txsLabel: txs.join(", "),
        animalName,
      });
    }
  });

  return { countPerGroup };
}

function abacusCounts(detCountsFetched: DetectionCountAPIResStandard): DetectionCountAbacus[] {
  const countPerGroup: DetectionCountAbacus[] = [];
  detCountsFetched.periodMs.forEach((dt, idx) => {
    countPerGroup.push({
      dt,
      count: detCountsFetched.count[idx],
      rx: detCountsFetched.serial[idx],
      tx: detCountsFetched.fullId[idx],
    });
  });
  return countPerGroup;
}

export function queryAbacus(
  filters: DetectionFilterInput,
  abacusOptions: DetectionCountOptions
): Thunk<void, DetectionAction> {
  return async (dispatch, getState) => {
    let payload: {
      countPerGroup: DetectionCountAbacus[] | DetectionCountAbacusAnimal[];
      filters: DetectionFilterInput;
    } = { countPerGroup: [], filters };

    // Early exit: if both receiver and transmitter arrays are empty, set no data and skip fetch
    if (
      filters.includeReceiverSerials?.length === 0 &&
      filters.includeTransmitterIDs?.length === 0
    ) {
      dispatch({ type: "DETECTION_SET_ABACUS_DATA", payload });
      return;
    }

    if (getState().detection.abacus.status === "UNLOADED") {
      dispatch({ type: "DETECTION_SET_ABACUS_INIT_LOADING", payload: { filters } });
    } else {
      dispatch({ type: "DETECTION_SET_ABACUS_LOADING", payload: { filters } });
    }

    try {
      const detCountsFetched = await fetchDetectionCountsPerIntervalGql(filters, abacusOptions);

      if (detCountsFetched?.periodMs?.length && detCountsFetched?.count?.length) {
        if (abacusOptions.byAnimal) {
          const animals = getState().animals.animals;
          const { countPerGroup } = abacusCountsAnimal(
            detCountsFetched as DetectionCountAPIResAnimal,
            animals
          );
          payload = { countPerGroup, filters };
        } else {
          const countPerGroup = abacusCounts(detCountsFetched as DetectionCountAPIResStandard);
          payload = { countPerGroup, filters };
        }
      }

      dispatch({ type: "DETECTION_SET_ABACUS_DATA", payload });
    } catch (ex) {
      dispatch(handleGqlErrors(ex));
      return;
    }
  };
}

export function clearDetectionData(): Thunk<void, DetectionAction> {
  return dispatch => {
    dispatch({
      type: "DETECTION_CLEAR_DATA",
    });
  };
}

export function getDetCountApi(
  filters: DetectionFilterInput,
  useTimeCorrection: boolean
): Thunk<void, DetectionAction> {
  return async (dispatch, getState) => {
    // Early exit: if both receiver and transmitter arrays are empty, set count to 0 and skip fetch
    if (
      filters.includeReceiverSerials?.length === 0 &&
      filters.includeTransmitterIDs?.length === 0
    ) {
      dispatch({ type: "DETECTION_SET_COUNT", payload: { count: 0, timeCorrectedCount: 0 } });
      return;
    }

    dispatch({ type: "DETECTION_COUNT_LOADING", payload: { filters } });

    const detCountsQuery = gql`
      query detCounts($filters: DetectionFilterInput, $useTimeCorrection: Boolean) {
        detectionCounts(filters: $filters, correctedTime: $useTimeCorrection) {
          totalCount
          timeCorrectedCount
        }
      }
    `;

    try {
      const detCounts = await callGqlApi(detCountsQuery, { filters, useTimeCorrection });
      // if there were multiple changes that affect this query, make sure we're displaying counts for the most recent state
      const lastFilters = getState().detection.count.filters;
      const lastUseTimeCorrection: boolean = getState().user.useTimeCorrection;
      if (isEqual(lastFilters, filters) && lastUseTimeCorrection === useTimeCorrection) {
        dispatch({
          type: "DETECTION_SET_COUNT",
          payload: {
            count: detCounts.detectionCounts.totalCount,
            timeCorrectedCount: detCounts.detectionCounts.timeCorrectedCount,
          },
        });
      }
    } catch (e) {
      console.error("GQL ERROR: ", e);
    }
  };
}

/** Shows or hides the download notification depending on the downloads state */
function toggleDetectionDownloadSnack(): Thunk<void, SnackBarAction> {
  return (dispatch, getState) => {
    const notifications = getState().snackbar.notifications;
    const downloads = getState().detection.downloads;
    // if there are downloads in the state and the notification isn't present, invoke it
    if (Object.keys(downloads).length) {
      if (notifications && !notifications.some(n => n.componentId === DOWNLOAD_SNACKBAR_KEY)) {
        dispatch(customSnackbar(DOWNLOAD_SNACKBAR_KEY, {}, DOWNLOAD_SNACKBAR_KEY));
      }
    }
    // otherwise clear the notification
    else {
      dispatch(removeSnackbar(DOWNLOAD_SNACKBAR_KEY));
    }
  };
}

export function clearDownloadDetection(jobId: string): Thunk<void, DetectionAction> {
  return dispatch => {
    dispatch({ type: "DETECTION_DOWNLOAD_CLEAR", payload: { jobId } });
    dispatch(toggleDetectionDownloadSnack());
  };
}

export function startDownloadDetections(
  filters: DetectionFilterInput,
  detCount: number,
  includeMetadata: boolean
): Thunk<void, DetectionAction | SnackBarAction> {
  return async (dispatch, getState) => {
    let dlJob = {} as DetectionDownloadJob;
    let jobId = "";
    const fileName = `detections_${formatDateTimeFilename()}.csv`;
    const correctedTime = getState().user.useTimeCorrection;
    try {
      const { startDownloadDetections } = await callGqlApi(
        gql`
          mutation startDownloadDetections(
            $filters: DetectionFilterInput
            $includeMetadata: Boolean
            $includeFiles: Boolean
            $correctedTime: Boolean
          ) {
            startDownloadDetections(
              filters: $filters
              includeMetadata: $includeMetadata
              includeFiles: $includeFiles
              correctedTime: $correctedTime
            ) {
              id
              percentComplete
              complete
              apiPath
              error
              errorMsg
              creationTime
            }
          }
        `,
        { filters, includeMetadata, includeFiles: true, correctedTime }
      );
      dlJob = (startDownloadDetections || {}) as DetectionDownloadJob;
      jobId = dlJob.id?.toString() || "";

      // prepare download:
      dispatch({
        type: "DETECTION_DOWNLOAD_STATUS",
        payload: {
          jobId,
          fileName,
          detCount,
          stage: "PREPARE",
          percentComplete: dlJob.percentComplete,
          errorMsg: dlJob.errorMsg,
        },
      });
      dispatch(toggleDetectionDownloadSnack());

      // if not complete, poll server for status
      while (dlJob.error === false && dlJob.complete === false) {
        const { downloadJob } = await callGqlApi(
          gql`
            query getJob($jobId: ID!) {
              downloadJob(jobId: $jobId) {
                id
                percentComplete
                complete
                error
                errorMsg
                apiPath
              }
            }
          `,
          { jobId }
        );
        dlJob = (downloadJob || {}) as DetectionDownloadJob;

        // Update download prepare progress:
        dispatch({
          type: "DETECTION_DOWNLOAD_STATUS",
          payload: {
            jobId,
            stage: "PREPARE",
            percentComplete: dlJob.percentComplete,
            errorMsg: dlJob.errorMsg,
          },
        });

        // wait a bit and loop around unless complete or error
        if (dlJob.complete || dlJob.error === true || dlJob.error === undefined) {
          break;
        } else {
          await sleep(500);
        }
      }

      // Bail conditions:
      if (dlJob.error === true || dlJob.error === undefined) {
        console.error(`Detection download error preparing file:`, dlJob.errorMsg);
        dispatch({
          type: "DETECTION_DOWNLOAD_STATUS",
          payload: {
            jobId,
            stage: "ERROR",
            percentComplete: 0,
            errorMsg: dlJob.errorMsg,
          },
        });
        return;
      }

      // All good, start download
      if (dlJob.complete === true) {
        const url = `${GQL_SERVER_URL}${dlJob.apiPath}`;
        // update status: file download start:
        dispatch({
          type: "DETECTION_DOWNLOAD_STATUS",
          payload: {
            jobId,
            stage: "DOWNLOAD",
            percentComplete: 0,
            errorMsg: null,
          },
        });

        axios
          .get(url, {
            responseType: "blob",
            onDownloadProgress: progressEvent => {
              try {
                const { loaded } = progressEvent;
                const total = progressEvent.srcElement.getResponseHeader("x-file-size");

                if (total) {
                  const percentComplete = Math.floor((loaded / total) * 100);

                  //Update download progress:
                  dispatch({
                    type: "DETECTION_DOWNLOAD_STATUS",
                    payload: {
                      jobId,
                      stage: "DOWNLOAD",
                      percentComplete,
                    },
                  });
                }
              } catch (ex) {
                console.error(`Detection download error downloading file: `, ex);
                dispatch({
                  type: "DETECTION_DOWNLOAD_STATUS",
                  payload: {
                    jobId,
                    stage: "ERROR",
                    percentComplete: 0,
                    errorMsg: ex?.toString(),
                  },
                });
              }
            },
          })
          .then(({ data }) => {
            dispatch({
              type: "DETECTION_DOWNLOAD_STATUS",
              payload: {
                jobId,
                stage: "COMPLETE",
                percentComplete: 100,
                errorMsg: null,
              },
            });
            // remove the item from the store once complete
            setTimeout(() => {
              dispatch(clearDownloadDetection(jobId));
            }, 2000);
            createDownloadFromBlob(data, fileName);
          });
      }
    } catch (ex) {
      console.error(`Detection download uncaught error: `, ex);
      dispatch({
        type: "DETECTION_DOWNLOAD_STATUS",
        payload: {
          jobId,
          stage: "ERROR",
          errorMsg: ex?.toString(),
        },
      });
    }
  };
}
